import { useApolloClient, useMutation } from "@apollo/client";
import { gql } from "generated-graphql";
import {
  ScoutPrivateDocumentExtractionStatus,
  ScoutPrivateDocumentIngestionMode,
  type GetScoutConversationPrivateDocumentsQuery,
} from "generated-graphql/graphql";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { isScoutOptimisticConversationId } from "./scoutConversationIds";
import { scoutConversationStore } from "./scoutConversationStore";
import {
  appendScoutPrivateDocumentRows,
  GET_SCOUT_CONVERSATION_PRIVATE_DOCUMENTS,
  isLargePdfExtractionAttachment,
  mergeScoutPrivateDocumentRows,
  pickLargeDocumentJob,
  pickLargeDocumentOptInCandidates,
  shouldResetPrivateDocumentsOnConversationChange,
  upsertScoutPrivateDocumentRows,
} from "./scoutPrivateDocumentQuery";
import { useScoutPrivateDocumentStream } from "./useScoutPrivateDocumentStream";

export type ScoutConversationPrivateDocumentRow = {
  id: string;
  filename: string;
  sizeBytes?: number;
  pageCount?: number | null;
  ingestionMode: ScoutPrivateDocumentIngestionMode;
  extractionStatus: ScoutPrivateDocumentExtractionStatus;
  previewSummary?: string | null;
  fileExtension?: string;
  mediaType?: string;
  signedDownloadUrl?: string | null;
  thumbnailSignedUrl?: string | null;
  extractionProgress?: {
    pagesCompleted: number;
    pagesTotal?: number | null;
  } | null;
};

export type TrackLargeDocumentAttachmentInput = {
  privateDocumentId: string;
  filename: string;
  sizeBytes: number;
  ingestionMode?: string;
  extractionStatus?: string;
};

const CONFIRM_FULL_ANALYSIS = gql(`
  mutation ConfirmScoutPrivateDocumentFullAnalysis(
    $input: ConfirmScoutPrivateDocumentFullAnalysisInput!
  ) {
    confirmScoutPrivateDocumentFullAnalysis(input: $input) {
      id
      filename
      ingestionMode
      extractionStatus
      previewSummary
      pageCount
      sizeBytes
      thumbnailSignedUrl
      signedDownloadUrl
      extractionProgress {
        pagesCompleted
        pagesTotal
      }
    }
  }
`);

const DECLINE_FULL_ANALYSIS = gql(`
  mutation DeclineScoutPrivateDocumentFullAnalysis(
    $input: DeclineScoutPrivateDocumentFullAnalysisInput!
  ) {
    declineScoutPrivateDocumentFullAnalysis(input: $input) {
      id
      filename
      ingestionMode
      extractionStatus
      previewSummary
      pageCount
      sizeBytes
      thumbnailSignedUrl
      signedDownloadUrl
      extractionProgress {
        pagesCompleted
        pagesTotal
      }
    }
  }
`);

function mapTrackedAttachmentRow(
  attachment: TrackLargeDocumentAttachmentInput
): ScoutConversationPrivateDocumentRow | undefined {
  if (!isLargePdfExtractionAttachment(attachment)) {
    return undefined;
  }

  const extractionStatus =
    attachment.extractionStatus === ScoutPrivateDocumentExtractionStatus.PreviewRunning ||
    attachment.extractionStatus === ScoutPrivateDocumentExtractionStatus.AwaitingConfirmation ||
    attachment.extractionStatus === ScoutPrivateDocumentExtractionStatus.FullAnalysisDeclined ||
    attachment.extractionStatus === ScoutPrivateDocumentExtractionStatus.NotAttempted
      ? attachment.extractionStatus
      : ScoutPrivateDocumentExtractionStatus.NotAttempted;

  return {
    id: attachment.privateDocumentId,
    filename: attachment.filename,
    sizeBytes: attachment.sizeBytes,
    pageCount: null,
    ingestionMode: ScoutPrivateDocumentIngestionMode.LargePdfExtraction,
    extractionStatus,
    previewSummary: null,
    extractionProgress: null,
    thumbnailSignedUrl: null,
  };
}

function syncPrivateDocumentStatuses(
  conversationId: string,
  rows: ScoutConversationPrivateDocumentRow[]
): void {
  if (rows.length === 0) {
    return;
  }

  scoutConversationStore.updatePrivateDocumentStatuses({
    conversationId,
    statuses: rows.map((document) => ({
      id: document.id,
      ingestionMode: document.ingestionMode,
      extractionStatus: document.extractionStatus,
      pageCount: document.pageCount,
      thumbnailSignedUrl: document.thumbnailSignedUrl,
      extractionProgress: document.extractionProgress,
    })),
  });
}

const PREVIEW_COMPLETE_REFETCH_DEBOUNCE_MS = 300;

/**
 * Conversation private-document state for opt-in and file-chip labels.
 * Subscribes to the private-document SSE stream while large PDF work is in flight.
 */
export function useScoutConversationPrivateDocuments(input: {
  tenantId: string | undefined;
  conversationId: string | undefined;
}) {
  const apolloClient = useApolloClient();
  const [documents, setDocuments] = useState<ScoutConversationPrivateDocumentRow[]>([]);
  const [privateDocumentsHydrated, setPrivateDocumentsHydrated] = useState(false);
  const [confirmAnalysis, { loading: isConfirming }] = useMutation(CONFIRM_FULL_ANALYSIS);
  const [declineAnalysis, { loading: isDeclining }] = useMutation(DECLINE_FULL_ANALYSIS);
  const conversationIdRef = useRef(input.conversationId);
  conversationIdRef.current = input.conversationId;
  const documentsRef = useRef(documents);
  documentsRef.current = documents;
  const previousConversationIdRef = useRef(input.conversationId);
  const previewRefreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const applyDocumentRows = useCallback((rows: ScoutConversationPrivateDocumentRow[]) => {
    const mergedRows = mergeScoutPrivateDocumentRows(documentsRef.current, rows);
    documentsRef.current = mergedRows;
    setDocuments(mergedRows);
    return mergedRows;
  }, []);

  const upsertDocumentRows = useCallback((rows: ScoutConversationPrivateDocumentRow[]) => {
    const mergedRows = upsertScoutPrivateDocumentRows(documentsRef.current, rows);
    documentsRef.current = mergedRows;
    setDocuments(mergedRows);
    return mergedRows;
  }, []);

  const refreshPrivateDocuments = useCallback(
    async (conversationIdOverride?: string): Promise<ScoutConversationPrivateDocumentRow[]> => {
      const conversationId = conversationIdOverride ?? conversationIdRef.current;
      if (!input.tenantId || !conversationId || isScoutOptimisticConversationId(conversationId)) {
        return documentsRef.current;
      }

      const result = await apolloClient.query<GetScoutConversationPrivateDocumentsQuery>({
        query: GET_SCOUT_CONVERSATION_PRIVATE_DOCUMENTS,
        variables: {
          tenantId: input.tenantId,
          conversationId,
        },
        fetchPolicy: "network-only",
      });

      const rows = result.data?.getScoutConversationPrivateDocuments ?? [];
      const mergedRows = applyDocumentRows(rows);

      syncPrivateDocumentStatuses(conversationId, rows);

      return mergedRows;
    },
    [apolloClient, applyDocumentRows, input.tenantId]
  );

  const trackLargeDocumentAttachments = useCallback(
    (attachments: TrackLargeDocumentAttachmentInput[] | undefined) => {
      if (!attachments || attachments.length === 0) {
        return;
      }

      const rows = attachments
        .map(mapTrackedAttachmentRow)
        .filter((row): row is ScoutConversationPrivateDocumentRow => row !== undefined);

      if (rows.length === 0) {
        return;
      }

      const mergedRows = appendScoutPrivateDocumentRows(documentsRef.current, rows);
      documentsRef.current = mergedRows;
      setDocuments(mergedRows);

      const conversationId = conversationIdRef.current;
      if (conversationId) {
        syncPrivateDocumentStatuses(conversationId, rows);
      }
    },
    []
  );

  const removeTrackedLargeDocumentAttachments = useCallback((privateDocumentIds: string[]) => {
    if (privateDocumentIds.length === 0) {
      return;
    }

    const idsToRemove = new Set(privateDocumentIds);
    const mergedRows = documentsRef.current.filter((row) => !idsToRemove.has(row.id));
    documentsRef.current = mergedRows;
    setDocuments(mergedRows);
  }, []);

  useEffect(() => {
    const previousConversationId = previousConversationIdRef.current;
    const nextConversationId = input.conversationId;
    previousConversationIdRef.current = nextConversationId;

    if (
      shouldResetPrivateDocumentsOnConversationChange(previousConversationId, nextConversationId)
    ) {
      setDocuments([]);
      documentsRef.current = [];
    }

    if (!input.tenantId) {
      setDocuments([]);
      documentsRef.current = [];
      setPrivateDocumentsHydrated(false);
      return;
    }

    if (!input.conversationId || isScoutOptimisticConversationId(input.conversationId)) {
      setPrivateDocumentsHydrated(false);
      return;
    }

    let cancelled = false;
    setPrivateDocumentsHydrated(false);
    void refreshPrivateDocuments(input.conversationId).finally(() => {
      if (!cancelled) {
        setPrivateDocumentsHydrated(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [input.conversationId, input.tenantId, refreshPrivateDocuments]);

  const handleStreamDocumentUpdates = useCallback(
    (updates: ScoutConversationPrivateDocumentRow[]) => {
      upsertDocumentRows(updates);
      const conversationId = conversationIdRef.current;
      if (conversationId) {
        syncPrivateDocumentStatuses(conversationId, updates);
      }

      const shouldRefreshDocuments = updates.some(
        (update) =>
          update.extractionStatus === ScoutPrivateDocumentExtractionStatus.AwaitingConfirmation ||
          (update.pageCount !== undefined && update.pageCount !== null)
      );
      if (shouldRefreshDocuments && conversationId) {
        if (previewRefreshTimeoutRef.current) {
          clearTimeout(previewRefreshTimeoutRef.current);
        }
        previewRefreshTimeoutRef.current = setTimeout(() => {
          void refreshPrivateDocuments(conversationId);
        }, PREVIEW_COMPLETE_REFETCH_DEBOUNCE_MS);
      }
    },
    [upsertDocumentRows, refreshPrivateDocuments]
  );

  useEffect(() => {
    return () => {
      if (previewRefreshTimeoutRef.current) {
        clearTimeout(previewRefreshTimeoutRef.current);
      }
    };
  }, []);

  useScoutPrivateDocumentStream({
    tenantId: input.tenantId,
    conversationId: input.conversationId,
    privateDocumentsHydrated,
    documents,
    onDocumentUpdates: handleStreamDocumentUpdates,
  });

  const dismissLargeDocumentOptIn = useCallback(
    async (privateDocumentIds: string[]) => {
      const conversationId = conversationIdRef.current;
      const uniquePrivateDocumentIds = [...new Set(privateDocumentIds)];
      if (!input.tenantId || !conversationId || uniquePrivateDocumentIds.length === 0) {
        return;
      }

      const result = await declineAnalysis({
        variables: {
          input: {
            tenantId: input.tenantId,
            conversationId,
            privateDocumentIds: uniquePrivateDocumentIds,
          },
        },
      });

      const declined = result.data?.declineScoutPrivateDocumentFullAnalysis ?? [];
      if (declined.length > 0) {
        upsertDocumentRows(declined);

        scoutConversationStore.updatePrivateDocumentStatuses({
          conversationId,
          statuses: declined.map((row) => ({
            id: row.id,
            ingestionMode: row.ingestionMode,
            extractionStatus: row.extractionStatus,
            pageCount: row.pageCount,
            thumbnailSignedUrl: row.thumbnailSignedUrl,
            extractionProgress: row.extractionProgress,
          })),
        });
      }

      await refreshPrivateDocuments(conversationId);
    },
    [upsertDocumentRows, declineAnalysis, input.tenantId, refreshPrivateDocuments]
  );

  const largeDocumentOptIns = useMemo(
    () => pickLargeDocumentOptInCandidates(documents),
    [documents]
  );

  const largeDocumentJob = useMemo(() => pickLargeDocumentJob(documents), [documents]);

  const confirmFullAnalysis = useCallback(
    async (privateDocumentIds: string[]) => {
      const conversationId = conversationIdRef.current;
      const uniquePrivateDocumentIds = [...new Set(privateDocumentIds)];
      if (!input.tenantId || !conversationId || uniquePrivateDocumentIds.length === 0) {
        return;
      }

      const result = await confirmAnalysis({
        variables: {
          input: {
            tenantId: input.tenantId,
            conversationId,
            privateDocumentIds: uniquePrivateDocumentIds,
          },
        },
      });

      const confirmed = result.data?.confirmScoutPrivateDocumentFullAnalysis ?? [];
      if (confirmed.length > 0) {
        upsertDocumentRows(confirmed);

        scoutConversationStore.updatePrivateDocumentStatuses({
          conversationId,
          statuses: confirmed.map((row) => ({
            id: row.id,
            ingestionMode: row.ingestionMode,
            extractionStatus: row.extractionStatus,
            pageCount: row.pageCount,
            thumbnailSignedUrl: row.thumbnailSignedUrl,
            extractionProgress: row.extractionProgress,
          })),
        });
      }

      await refreshPrivateDocuments(conversationId);
    },
    [upsertDocumentRows, confirmAnalysis, input.tenantId, refreshPrivateDocuments]
  );

  return {
    documents,
    largeDocumentJob,
    largeDocumentOptIns,
    dismissLargeDocumentOptIn,
    confirmFullAnalysis,
    isConfirming,
    isDeclining,
    refreshPrivateDocuments,
    trackLargeDocumentAttachments,
    removeTrackedLargeDocumentAttachments,
  };
}
